import React, { useState, useEffect } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, InputBase, TextField, Typography } from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { useStatusPalette } from '../../utils/statusColors';
import ScheduleSessionLink from './ScheduleSessionLink';

function buildScheduleStatusStyles(p) {
  return {
    SCHEDULED: { ...p.purple, label: 'Scheduled' },
    RESUMING:  { ...p.blue, label: 'Resuming' },
    COMPLETED: { ...p.green, label: 'Completed' },
    CANCELLED: { ...p.zinc, label: 'Cancelled' },
    ERROR:     { ...p.red, label: 'Error' },
  };
}

export function ScheduleStatusBadge({ status }) {
  const _sp = useStatusPalette();
  const styles = buildScheduleStatusStyles(_sp);
  const s = styles[status] || styles.SCHEDULED;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        height: '20px',
        px: '6px',
        borderRadius: '5px',
        backgroundColor: s.bg,
        border: `1px solid ${s.border}`,
      }}
    >
      <Box sx={{ width: 5, height: 5, borderRadius: '2px', backgroundColor: s.color, flexShrink: 0 }} />
      <Typography sx={{ fontSize: '11px', fontWeight: 500, color: s.color, lineHeight: 1, whiteSpace: 'nowrap' }}>
        {s.label}
      </Typography>
    </Box>
  );
}

export function toLocalDatetimeString(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function ScheduledMessageDialog({ item, open, onClose, onEdit, onCancel }) {
  const _sp = useStatusPalette();
  const [editMessage, setEditMessage] = useState('');
  const [editResumeAt, setEditResumeAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (open && item) {
      setEditMessage(item.message || '');
      setEditResumeAt(toLocalDatetimeString(item.resumeAt));
      setSaving(false);
      setCancelling(false);
    }
  }, [open, item?.scheduleId]);

  if (!item) return null;

  const isEditable = item.status === 'SCHEDULED';

  const handleSave = async () => {
    const payload = {};
    if (editMessage.trim() && editMessage.trim() !== (item.message || '')) payload.message = editMessage.trim();
    if (editResumeAt) {
      const newIso = new Date(editResumeAt).toISOString();
      const oldIso = item.resumeAt ? new Date(item.resumeAt).toISOString() : '';
      if (newIso !== oldIso) payload.resumeAt = newIso;
    }
    if (Object.keys(payload).length > 0 && onEdit) {
      setSaving(true);
      await onEdit(item.scheduleId, payload);
      setSaving(false);
    }
    onClose();
  };

  const handleCancel = async () => {
    if (!onCancel) return;
    setCancelling(true);
    await onCancel(item.scheduleId);
    setCancelling(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: '12px', boxShadow: '0 8px 30px rgba(0,0,0,0.12)' },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pb: 1,
          fontSize: '15px',
          fontWeight: 600,
          color: 'text.primary',
        }}
      >
        <ScheduleIcon sx={{ fontSize: 20, color: _sp.purple.color }} />
        Scheduled message
        <Box sx={{ flex: 1 }} />
        <ScheduleStatusBadge status={item.status} />
      </DialogTitle>

      <DialogContent sx={{ pt: '8px !important', pb: 1 }}>
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'text.primary', mb: 0.75 }}>
          Message
        </Typography>
        {isEditable ? (
          <InputBase
            value={editMessage}
            onChange={(e) => setEditMessage(e.target.value)}
            multiline
            minRows={2}
            maxRows={8}
            fullWidth
            sx={{
              fontSize: '14px',
              border: '1px solid', borderColor: 'divider',
              borderRadius: '8px',
              px: 1.5,
              py: 1,
              mb: 2,
              backgroundColor: 'background.paper',
              '&:focus-within': { borderColor: 'text.disabled' },
            }}
          />
        ) : (
          <Typography sx={{ fontSize: '14px', color: 'text.primary', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word', mb: 2, px: 0.5 }}>
            {item.message || 'No message'}
          </Typography>
        )}

        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'text.primary', mb: 0.75 }}>
          Send at
        </Typography>
        {isEditable ? (
          <TextField
            type="datetime-local"
            value={editResumeAt}
            onChange={(e) => setEditResumeAt(e.target.value)}
            size="small"
            fullWidth
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                fontSize: '14px',
              },
            }}
          />
        ) : (
          <Typography sx={{ fontSize: '14px', color: 'text.primary', mb: 2, px: 0.5 }}>
            {item.resumeAt
              ? new Date(item.resumeAt).toLocaleString(undefined, {
                  weekday: 'short', month: 'short', day: 'numeric',
                  hour: 'numeric', minute: '2-digit',
                })
              : '—'}
          </Typography>
        )}

        {item.reason && (
          <>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'text.primary', mb: 0.75 }}>
              Reason
            </Typography>
            <Typography sx={{ fontSize: '13px', color: 'text.disabled', fontStyle: 'italic', mb: 1, px: 0.5 }}>
              {item.reason}
            </Typography>
          </>
        )}

        <ScheduleSessionLink
          agentId={item.agentId}
          chatSessionId={item.chatSessionId}
          onNavigate={onClose}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, justifyContent: 'space-between' }}>
        {isEditable ? (
          <Button
            onClick={cancelling ? undefined : handleCancel}
            disabled={cancelling || saving}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 600,
              color: _sp.red.color,
              '&:hover': { backgroundColor: _sp.red.bg },
            }}
          >
            {cancelling ? 'Cancelling...' : 'Cancel schedule'}
          </Button>
        ) : (
          <Box />
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={onClose}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 500,
              color: 'text.disabled',
            }}
          >
            Close
          </Button>
          {isEditable && (
            <Button
              onClick={saving ? undefined : handleSave}
              disabled={saving || cancelling}
              variant="contained"
              disableElevation
              sx={{
                textTransform: 'none',
                fontSize: '13px',
                fontWeight: 600,
                backgroundColor: _sp.purple.color,
                borderRadius: '8px',
                px: 2.5,
                '&:hover': { backgroundColor: _sp.purple.color, opacity: 0.85 },
                '&.Mui-disabled': { backgroundColor: _sp.purple.color, opacity: 0.5, color: '#fff' },
              }}
            >
              {saving ? 'Saving...' : 'Save changes'}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
